import "./Summary.scss";

import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AddDeviceFormType } from "@/apps/partner/types";
import { transformFormDataToApiRequest } from "@/apps/partner/utils/formDataTransformation";
import {
  formatBirthday,
  getLanguageTranslationKey,
  getSalutationDisplayText,
} from "@/apps/partner/utils/partnerUtils";
import {
  ConnectionIcon,
  Info,
  SpeedIcon,
  VoiceOptionCall,
} from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { getHeader } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/roamingUtils";
import { useHandleError } from "@/hooks/useHandleError";
import { useSalesPartnerOrderSim } from "@/services/api";
import { addDeviceFormDataAtom } from "@/utils/atoms";
import { getTariffType, TariffTypes } from "@/utils/deviceUtils";
import { getIncludedFeaturesRateSelection } from "@/utils/tariffUtils";

interface FeatureDisplayProps {
  features: Array<{ name: string }>;
  isDataTariff: boolean;
  tariffSpeed: string;
}

const FeatureDisplay = ({
  features,
  tariffSpeed,
  isDataTariff,
}: FeatureDisplayProps) => {
  const iconClassName = "size-6 flex-shrink-0 text-primary-100";
  const { t } = useTranslation();

  return (
    <>
      {isDataTariff ? (
        <div className="mb-4 flex flex-col gap-4 text-base">
          <p className="font-semibold">{t("countries.CHE")}</p>
          <p className="flex flex-row gap-2">
            <ConnectionIcon className={iconClassName} />
            {features[0]?.name || "-"}
          </p>
          <p className="flex flex-row gap-2">
            <SpeedIcon className={iconClassName} />
            {t("partnerPortal:summary.upUntil", {
              amount: tariffSpeed?.split(" ")[0] || "",
              defaultValue: "Up to {{amount}}",
            })}
          </p>
        </div>
      ) : (
        <div className="mb-4 flex flex-col gap-4 text-base">
          <p className="font-semibold">{t("countries.CHE")}</p>

          <p className="flex flex-row gap-2">
            <ConnectionIcon className={iconClassName} />
            {features[0]?.name || "-"}
          </p>
          <p className="flex flex-row gap-2">
            <SpeedIcon className={iconClassName} />
            {features[2]?.name || "-"}
          </p>
          <p className="flex flex-row gap-2 pb-4">
            <VoiceOptionCall className={iconClassName} />
            {features[1]?.name || "-"}
          </p>

          <p className="font-semibold">
            {t("portal:mobileAbo.features.international")}
          </p>

          <p className="flex flex-row gap-2">
            <ConnectionIcon className={iconClassName} />
            {features.length >= 4 ? features[3]?.name : "-"}
          </p>
          <p className="flex flex-row gap-2">
            <VoiceOptionCall className={iconClassName} />
            {features.length > 4 ? features[4]?.name : "-"}
          </p>
        </div>
      )}
    </>
  );
};

interface ChangeButtonProps {
  section: "device" | "tariff" | "personal";
}

const ChangeButton = ({ section }: ChangeButtonProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["partnerPortal"], {
    keyPrefix: "summary",
  });

  const handleChangeClick = () => {
    // Navigate to the add-device page
    navigate("/partner/add-sim", {
      state: { scrollToSection: section },
    });
  };

  return (
    <button
      onClick={handleChangeClick}
      className="change-button cursor-pointer"
    >
      {t("change")}
    </button>
  );
};

const Summary = () => {
  const { t } = useTranslation(["partnerPortal"], {
    keyPrefix: "summary",
  });
  const setAddDeviceFormData = useSetAtom(addDeviceFormDataAtom);
  const formData = useAtomValue(
    addDeviceFormDataAtom,
  ) as AddDeviceFormType | null;

  const navigate = useNavigate();
  const handleError = useHandleError();

  // State for checkbox
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { mutate: orderSim, isLoading } = useSalesPartnerOrderSim({
    mutation: {
      onSuccess: () => {
        setAddDeviceFormData(null);
        // Set success flag to allow access to success page
        sessionStorage.setItem("orderSuccess", "true");
        navigate("/partner/add-sim/success");
      },
      onError: (error) => {
        // TODO: more specific error handling?
        handleError(error);
      },
    },
  });

  const tariffType = getTariffType(formData?.productId);

  const includedFeatures = getIncludedFeaturesRateSelection(t, tariffType, 30);

  // Handle checkbox change
  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
    if (checked) {
      setTermsError("");
    }
  };

  // Handle form submission
  const handleSubmit = (formData: AddDeviceFormType) => {
    if (!termsAccepted) {
      setTermsError(t("termsAndConditionsError"));

      return;
    }
    try {
      // Get header information for the API request
      const header = getHeader();

      // Transform form data to API format using our utility function
      const apiRequestData = transformFormDataToApiRequest(formData, header);

      // Submit the order
      orderSim({ data: apiRequestData });
    } catch (error) {
      handleError(error);

      return;
    }
  };

  // If no form data is available, navigate to the add-device page
  useEffect(() => {
    if (!formData) {
      navigate("/partner/add-sim");
    }
  }, [formData, navigate]);
  if (!formData) return null;

  return (
    <div id="summary-wrapper" className={clsx("tile-width-wrapper")}>
      <div className="management-tile active">
        <div className="mb-10 flex flex-row justify-between pt-10">
          <h1 className="text-4xl font-semibold text-primary-100">
            {t("title")}
          </h1>
          {/* TODO: reactivate when we fix the printing layout */}
          {/* <Button onClick={() => window.print()} className="accent inverted">
            <div className="flex items-center justify-center">
              <Print className="text-primary-100" />
              <span className="ml-2">{t("print")}</span>
            </div>
          </Button> */}
        </div>
        <div className="background-image mb-12 flex flex-col p-3 text-center">
          <span className="text-xl font-semibold text-white">
            {t("discount")}
          </span>
          <div className="flex items-center justify-center">
            <Trans
              i18nKey="partnerPortal:summary.discount2"
              components={[
                <h2
                  key="percentage"
                  className="font-semibold text-primary-100"
                />,
              ]}
              values={{
                // TODO: get disocount from endpoint when ready
                discount: "20",
              }}
            />
            <Info
              key="infoIcon"
              className="ml-2 text-primary-100"
              width={24}
              height={24}
            />
          </div>
          <p className="font-small pt-1.5 font-semibold text-white">
            {t("hint")}
          </p>
        </div>
        <div className="flex flex-col justify-between gap-2 xl:w-1/2">
          <div className="mb-6 flex flex-row justify-between">
            <h3>{t("choice")}</h3>
          </div>

          <div className="mb-8 flex flex-row justify-between gap-4">
            <div className="flex flex-col gap-2">
              <span>{t("assignedDevice")}</span>
              <span className="font-semibold text-primary-100">
                {formData?.device} | {formData?.simAlias}
              </span>
            </div>

            <ChangeButton section="device" />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between gap-2">
              <span>{t("tariffOptions")}</span>
              <ChangeButton section="tariff" />
            </div>
            <span className="font-semibold text-primary-100">
              {formData?.tariffName}
            </span>
          </div>
          <div className="mb-8 border-l-2 border-primary-100 pl-6">
            <FeatureDisplay
              isDataTariff={tariffType === TariffTypes.dataTariff}
              tariffSpeed={formData?.tariffSpeed || ""}
              features={includedFeatures}
            />
          </div>
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <span>{t("nowToPay")}</span>
              <span className="font-semibold text-primary-100">CHF 0.00</span>
            </div>
            <div className="flex flex-col gap-2">
              <span>{t("payMonthly")}</span>
              <span className="flex flex-row gap-2 text-primary-100">
                <span className="line-through">{formData?.tariffPrice}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.tariffPrice &&
                    (() => {
                      const price = parseFloat(
                        formData.tariffPrice.replace("CHF", ""),
                      );

                      return !isNaN(price) && `CHF ${(price * 0.8).toFixed(2)}`;
                    })()}
                </span>
              </span>
            </div>
          </div>
          <div className="mb-8 grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <span>{t("duration")}</span>
              <span className="font-semibold text-primary-100">
                {t("30days")}
              </span>
            </div>
            {formData?.onp && formData?.mnpData && (
              <div className="flex flex-col gap-2">
                <span>{t("numberPorting")}</span>
                <span className="font-semibold text-primary-100">
                  <span>
                    {formData.mnpData.currentProvider.description} |{" "}
                    {formData.mnpData.portedPhoneNumber.phone}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <hr className="my-12 border-primary-25" />
        <div className="mb-8 flex flex-row justify-between gap-4 xl:w-1/2">
          <h3>{t("personal")}</h3>
          <ChangeButton section="personal" />
        </div>

        {formData?.isExistingCustomer ? (
          <div className="grid grid-cols-1 gap-6 xl:w-1/2">
            <div className="flex flex-col gap-2">
              <span>{t("personalDetails.email")}</span>
              <span className="font-semibold text-primary-100">
                {formData?.mail}
              </span>
            </div>
            <p className="text-base text-primary-100">
              {t("existingCustomerSummary")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:w-1/2">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.email")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.mail}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span>
                  {t("translation:user-account.inbox.contactlanguage" as any)}
                </span>
                <span className="font-semibold text-primary-100">
                  {t(
                    `translation:user-account.inbox.languages.${getLanguageTranslationKey(formData?.customer?.languageEnum || "")}` as any,
                  )}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span>{t("personalDetails.formOfAddress")}</span>
              <span className="font-semibold text-primary-100">
                {getSalutationDisplayText(
                  formData?.customer?.salutationEnum,
                  t,
                )}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.firstName")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.firstName}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.lastName")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.lastName}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.street")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.street}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.houseNumber")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.houseNumber}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.postalDeliveryNotes")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.postalDeliveryNotes || "-"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.zip")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.zip}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.city")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.city}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span>{t("personalDetails.country")}</span>
              <span className="font-semibold text-primary-100">
                {t(
                  `translation:countries.${formData?.customer?.countryCodeAlpha2.ALPHA3}`,
                )}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span>{t("personalDetails.phone")}</span>
                <span className="font-semibold text-primary-100">
                  {formData?.customer?.contactPhoneNumber}
                </span>
              </div>
              <div className="mt-auto flex flex-col gap-2">
                <span>{t("personalDetails.dateOfBirth")}</span>
                <span className="mt-auto font-semibold text-primary-100">
                  {formatBirthday(formData?.customer?.birthday)}
                </span>
              </div>
            </div>
          </div>
        )}
        <hr className="my-12 border-primary-25" />

        <div className="flex items-start gap-3">
          <div className="relative">
            <input
              type="checkbox"
              id="termsAgreement"
              checked={termsAccepted}
              onChange={(e) => handleTermsChange(e.target.checked)}
              aria-label="Accept terms and conditions"
              className="absolute z-10 h-6 w-6 cursor-pointer opacity-0"
            />
            <div
              className={clsx("mr-4 h-6 w-6 border-1 transition-colors", {
                "border-error-100": termsError,
                "border-primary-100": !termsError,
              })}
            >
              {termsAccepted && (
                <div className="flex h-full items-center justify-center">
                  <svg
                    className="h-4 w-4 text-primary-100"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
          <label htmlFor="termsAgreement" className="text-base">
            <Trans
              components={[
                <a
                  target="_blank"
                  key="termsAndConditionsLink"
                  // TODO: later add link to terms and conditions
                  href="#"
                  className="text-primary-100 underline"
                  rel="noreferrer"
                  title="AGBs"
                />,
              ]}
              i18nKey="partnerPortal:summary.acceptTerms"
            />
          </label>
        </div>
        {termsError && (
          <p className="mt-1 text-center text-sm text-error-100">
            {termsError}
          </p>
        )}

        <div className="flex flex-row justify-end gap-4 pt-10">
          {/* <Button onClick={() => window.print()} className="accent inverted">
            <div className="flex items-center justify-center">
              <Print className="text-primary-100" />
              <span className="ml-2">{t("print")}</span>
            </div>
          </Button> */}

          <Button
            className="accent"
            disabled={isLoading}
            onClick={() => handleSubmit(formData)}
          >
            <span className="flex w-full items-center justify-center">
              {t("submit")}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Summary;
